import {
  DebtCaseFile,
  DebtDecision,
  DebtInvoice,
  DebtTimelineKind,
  DebtorInput,
  DebtorPreview,
  WithdrawReason,
  useDebtAnswerFeedbackMutation,
  useDebtCaseFileQuery,
  useDebtDecideMutation,
  useDebtHandOverMutation,
  useDebtReportDirectPaymentMutation,
  useDebtWithdrawMutation,
  useDebtWithdrawalFeesLazyQuery,
} from '@dieterApi/debtCollection';
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  DEBTIST_ACTIVITY_LABELS,
  DEBTIST_STATUS_LABELS,
  EVENT_LABELS,
  RECOMMENDATION_LABELS,
  SIGNAL_CHIP,
  SUBSCRIPTION_LABELS,
  WITHDRAW_REASON_LABELS,
  WORKFLOW_LABELS,
  formatDate,
  formatEur,
  handoverTargetLabel,
  stripeCustomerUrl,
} from './debtCollectionLabels';

// Kundenakte im Inkasso-Cockpit: alles für die Entscheidung auf einer Seite,
// darunter die Entscheidungsleiste (übergeben, zurückstellen, selbst klären,
// ausbuchen). Lädt immer frisch (Stripe, Sequence Engine, Chatwoot, Fall).

const TIMELINE_LABELS: Record<DebtTimelineKind, string> = {
  invoice_paid: 'Zahlung',
  invoice_open: 'Offen',
  payment_failed: 'Fehlschlag',
  mail: 'Mail',
  support: 'Support',
  login: 'Login',
  contract: 'Vertrag',
  cancellation: 'Kündigung',
};

const INVOICE_STATUS: Record<string, string> = { open: 'offen', uncollectible: 'uneinbringlich' };

const DEBTOR_FIELDS: Array<[keyof DebtorInput, string]> = [
  ['businessName', 'Firma'],
  ['contactName', 'Kontakt'],
  ['email', 'E-Mail'],
  ['vatId', 'USt-ID'],
  ['addressLine1', 'Straße'],
  ['addressLine2', 'Adresszusatz'],
  ['postalCode', 'PLZ'],
  ['town', 'Ort'],
  ['country', 'Land (DEU)'],
];

const DECISION_TEXT: Record<Exclude<DebtDecision, 'REOPEN'>, { title: string; button: string; noteLabel: string }> = {
  HOLD: { title: 'Zurückstellen', button: 'Zurückstellen', noteLabel: 'Grund' },
  CLARIFY: {
    title: 'Selbst klären',
    button: 'Speichern',
    noteLabel: 'Was ist zu klären? (geht als Notiz nach Chatwoot)',
  },
  WRITE_OFF: { title: 'Ausbuchen', button: 'Ausbuchen', noteLabel: 'Grund (z. B. Insolvenz, Kulanz)' },
};

function Section({ title, children, action }: { title: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <h4 className="text-base font-medium">{title}</h4>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, missing }: { label: string; value: ReactNode; missing?: boolean }) {
  return (
    <div style={{ display: 'contents' }}>
      <div style={{ color: '#888' }}>{label}</div>
      <div style={{ color: missing ? '#c62828' : undefined }}>{value || (missing ? 'fehlt' : '—')}</div>
    </div>
  );
}

const inputFromPreview = (d: DebtorPreview): DebtorInput =>
  Object.fromEntries(DEBTOR_FIELDS.map(([key]) => [key, (d as any)[key] ?? ''])) as DebtorInput;

const debtorMissing = (d: DebtorInput) => [
  ...(d.businessName ? [] : ['Name']),
  ...(d.addressLine1 && d.postalCode && d.town ? [] : ['Anschrift']),
];

interface ContentProps {
  file: DebtCaseFile;
  debtor: DebtorInput;
  onDebtorChange: (debtor: DebtorInput) => void;
  editDebtor: boolean;
  onEditDebtor: () => void;
  selectedInvoices: string[];
  onToggleInvoice: (id: string) => void;
  canSelect: boolean;
  onDirectPayment?: (invoice: DebtInvoice) => void;
}

function CaseFileContent({
  file,
  debtor,
  onDebtorChange,
  editDebtor,
  onEditDebtor,
  selectedInvoices,
  onToggleInvoice,
  canSelect,
  onDirectPayment,
}: ContentProps) {
  const c = file.customer;
  const missing = debtorMissing(debtor);
  return (
    <>
      <Section title="Signale">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {c.signals.map((s) => (
            <Chip key={s.key} label={s.label} size="small" {...SIGNAL_CHIP[s.level]} />
          ))}
        </div>
        {!file.supportChecked && (
          <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 6 }}>
            Chatwoot war nicht erreichbar, der Support-Verlauf ist nicht geprüft.
          </div>
        )}
      </Section>

      <Section title={`Offene Rechnungen · ${formatEur(c.openAmount)}`}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {canSelect && <TableCell padding="checkbox" />}
              <TableCell>Rechnung</TableCell>
              <TableCell>Datum</TableCell>
              <TableCell>Fällig</TableCell>
              <TableCell align="right">Offen</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Abbuchung</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {c.invoices.map((i) => (
              <TableRow key={i.id}>
                {canSelect && (
                  <TableCell padding="checkbox">
                    {i.handoverStatus !== 'HANDED_OVER' && (
                      <Checkbox
                        size="small"
                        checked={selectedInvoices.includes(i.id)}
                        onChange={() => onToggleInvoice(i.id)}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell>
                  {i.hostedInvoiceUrl ? (
                    <Link href={i.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">
                      {i.number || i.id}
                    </Link>
                  ) : (
                    i.number || i.id
                  )}
                  {i.description && <div style={{ fontSize: '0.75rem', color: '#888' }}>{i.description}</div>}
                </TableCell>
                <TableCell>{formatDate(i.createdAt)}</TableCell>
                <TableCell>{formatDate(i.dueDate)}</TableCell>
                <TableCell align="right">{formatEur(i.amountRemaining)}</TableCell>
                <TableCell>
                  {i.handoverStatus === 'HANDED_OVER' ? (
                    <div>
                      <Chip label="bei Debtist" color="success" size="small" variant="outlined" />
                      {onDirectPayment && (
                        <Button
                          size="small"
                          sx={{ display: 'block', p: 0, minWidth: 0 }}
                          onClick={() => onDirectPayment(i)}
                        >
                          Direktzahlung melden
                        </Button>
                      )}
                    </div>
                  ) : i.handoverStatus === 'FAILED' ? (
                    <Chip label="Übergabe fehlgeschlagen" color="error" size="small" variant="outlined" />
                  ) : (
                    (INVOICE_STATUS[i.status] ?? i.status)
                  )}
                  {i.handoverError && <div style={{ fontSize: '0.75rem', color: '#c62828' }}>{i.handoverError}</div>}
                  {!i.handoverStatus && i.markedUncollectibleAt && (
                    <div style={{ fontSize: '0.75rem', color: '#888' }}>seit {formatDate(i.markedUncollectibleAt)}</div>
                  )}
                </TableCell>
                <TableCell>
                  {i.attemptCount} Versuche{i.failureLabel ? ` · ${i.failureLabel}` : ''}
                </TableCell>
                <TableCell>
                  {i.invoicePdf && (
                    <Link href={i.invoicePdf} target="_blank" rel="noopener noreferrer">
                      PDF
                    </Link>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
        <Section
          title="Schuldnerdaten für Debtist"
          action={
            canSelect && !editDebtor ? (
              <Button size="small" onClick={onEditDebtor}>
                Bearbeiten
              </Button>
            ) : null
          }
        >
          {editDebtor ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {DEBTOR_FIELDS.map(([key, label]) => (
                <TextField
                  key={key}
                  size="small"
                  label={label}
                  value={debtor[key] ?? ''}
                  error={
                    (key === 'businessName' && missing.includes('Name')) ||
                    (['addressLine1', 'postalCode', 'town'].includes(key) &&
                      missing.includes('Anschrift') &&
                      !debtor[key])
                  }
                  onChange={(event) => onDebtorChange({ ...debtor, [key]: event.target.value })}
                />
              ))}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: '0.875rem' }}>
              <Field label="Typ" value="Unternehmen (Business)" />
              <Field label="Firma" value={debtor.businessName} missing={missing.includes('Name')} />
              <Field label="Kontakt" value={debtor.contactName} />
              <Field label="E-Mail" value={debtor.email} />
              <Field label="USt-ID" value={debtor.vatId} />
              <Field
                label="Anschrift"
                missing={missing.includes('Anschrift')}
                value={
                  missing.includes('Anschrift')
                    ? null
                    : [
                        debtor.addressLine1,
                        debtor.addressLine2,
                        `${debtor.postalCode ?? ''} ${debtor.town ?? ''}`.trim(),
                        debtor.country,
                      ]
                        .filter(Boolean)
                        .join(', ')
                }
              />
            </div>
          )}
        </Section>
        <Section title="Kunde">
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: '0.875rem' }}>
            <Field label="Kunde seit" value={formatDate(c.customerSince)} />
            <Field
              label="Bisher bezahlt"
              value={c.paidInvoiceCount ? `${formatEur(c.paidTotal)} in ${c.paidInvoiceCount} Rechnungen` : 'nichts'}
            />
            <Field label="Letzte Zahlung" value={formatDate(c.lastPaidAt)} />
            <Field
              label="Abo (Stripe)"
              value={c.subscriptionStatuses.map((s) => SUBSCRIPTION_LABELS[s] ?? s).join(', ') || 'beendet'}
            />
            <Field label="Dieter-Plan" value={file.subscription} />
            <Field
              label="Dieter-Konto"
              value={
                c.companyName
                  ? `${c.companyName}${file.isAgency ? ' (Agentur)' : file.isTenant ? ' (Mandant)' : ''}`
                  : 'nicht verknüpft'
              }
            />
            <Field
              label="Nutzer"
              value={file.users.map((u) => `${u.email} (Login ${formatDate(u.lastLogin)})`).join(', ')}
            />
            <Field
              label="Stripe"
              value={
                <Link href={stripeCustomerUrl(c.stripeCustomerId)} target="_blank" rel="noopener noreferrer">
                  {c.stripeCustomerId}
                </Link>
              }
            />
            {c.debtistReference && <Field label="Debtist-Az." value={c.debtistReference} />}
          </div>
        </Section>
      </div>

      {file.events.length > 0 && (
        <Section title="Protokoll">
          <Table size="small">
            <TableBody>
              {file.events.map((e, index) => (
                <TableRow key={`${e.at}-${index}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap', width: 150 }}>
                    {new Date(e.at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                  </TableCell>
                  <TableCell>
                    {EVENT_LABELS[e.action] ?? e.action}
                    {e.note && <div style={{ fontSize: '0.75rem', color: '#888' }}>{e.note}</div>}
                  </TableCell>
                  <TableCell sx={{ color: '#888' }}>{e.actorEmail}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      )}

      <Section title="Verlauf">
        <Table size="small">
          <TableBody>
            {file.timeline.map((entry, index) => (
              <TableRow key={`${entry.at}-${index}`}>
                <TableCell sx={{ whiteSpace: 'nowrap', width: 100 }}>{formatDate(entry.at)}</TableCell>
                <TableCell sx={{ width: 90 }}>
                  <Chip label={TIMELINE_LABELS[entry.kind]} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  {entry.url ? (
                    <Link href={entry.url} target="_blank" rel="noopener noreferrer">
                      {entry.title}
                    </Link>
                  ) : (
                    entry.title
                  )}
                  {entry.detail && <div style={{ fontSize: '0.75rem', color: '#888' }}>{entry.detail}</div>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
    </>
  );
}

function DebtistSection({
  file,
  canAnswer,
  answering,
  onAnswer,
}: {
  file: DebtCaseFile;
  canAnswer: boolean;
  answering: boolean;
  onAnswer: (comment: string) => Promise<boolean>;
}) {
  const c = file.customer;
  const [answer, setAnswer] = useState('');
  return (
    <Section title={`Debtist · Az. ${c.debtistReference}`}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: '0.875rem' }}>
        <Field
          label="Status"
          value={
            c.debtistStatus ? (DEBTIST_STATUS_LABELS[c.debtistStatus] ?? c.debtistStatus) : 'noch nicht abgeglichen'
          }
        />
        {c.debtistTotalPaid !== null && <Field label="Gezahlt" value={formatEur(c.debtistTotalPaid)} />}
        {c.debtistTotalPending !== null && (
          <Field label="Noch offen (inkl. Gebühren)" value={formatEur(c.debtistTotalPending)} />
        )}
        {c.debtistClosure && <Field label="Abschlussgrund" value={c.debtistClosure} />}
        <Field
          label="Letzter Abgleich"
          value={
            c.debtistSyncedAt
              ? new Date(c.debtistSyncedAt).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
              : '—'
          }
        />
      </div>
      {c.feedbackRequired && (
        <div style={{ marginTop: 12, padding: 12, border: '1px solid #ed6c02', borderRadius: 8 }}>
          <div style={{ fontWeight: 500, marginBottom: 8 }}>Debtist hat eine Rückfrage (Details im Verlauf unten)</div>
          {canAnswer && (
            <>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Antwort an Debtist"
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
              />
              <Button
                variant="contained"
                size="small"
                sx={{ mt: 1 }}
                disabled={answering || !answer.trim()}
                onClick={async () => {
                  if (await onAnswer(answer)) setAnswer('');
                }}
              >
                Antwort senden
              </Button>
            </>
          )}
        </div>
      )}
      {file.debtistActivities.length > 0 && (
        <Table size="small" sx={{ mt: 1 }}>
          <TableBody>
            {file.debtistActivities.map((a, index) => (
              <TableRow key={`${a.at}-${index}`}>
                <TableCell sx={{ whiteSpace: 'nowrap', width: 100 }}>{formatDate(a.at)}</TableCell>
                <TableCell sx={{ width: 160 }}>
                  <Chip label={DEBTIST_ACTIVITY_LABELS[a.type] ?? a.type} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{a.comment || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}

const inDays = (days: number) => new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

interface Props {
  stripeCustomerId: string | null;
  handoverEnabled: boolean;
  handoverTarget: string | null;
  onClose: () => void;
  onChanged: () => void;
}

export function DebtCaseFileDialog({ stripeCustomerId, handoverEnabled, handoverTarget, onClose, onChanged }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const { data, loading, error, refetch } = useDebtCaseFileQuery(stripeCustomerId);
  const [handOver, { loading: handingOver }] = useDebtHandOverMutation();
  const [decide, { loading: deciding }] = useDebtDecideMutation();
  const [answerFeedback, { loading: answering }] = useDebtAnswerFeedbackMutation();
  const [reportPayment, { loading: reporting }] = useDebtReportDirectPaymentMutation();
  const [withdraw, { loading: withdrawing }] = useDebtWithdrawMutation();
  const [loadFees, { data: feesData, loading: loadingFees, error: feesError }] = useDebtWithdrawalFeesLazyQuery();
  const [paymentInvoice, setPaymentInvoice] = useState<DebtInvoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(inDays(0));
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState<WithdrawReason>('CreditorWishWithoutReason');
  const file = data?.debtCaseFile ?? null;
  const customer = file?.customer;

  const [debtor, setDebtor] = useState<DebtorInput>({});
  const [editDebtor, setEditDebtor] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [confirmHandover, setConfirmHandover] = useState(false);
  const [decision, setDecision] = useState<Exclude<DebtDecision, 'REOPEN'> | null>(null);
  const [note, setNote] = useState('');
  const [holdUntil, setHoldUntil] = useState(inDays(30));

  // Beim Laden einer Akte: Schuldnerdaten aus der Vorschau, alle noch nicht
  // übergebenen Rechnungen vorausgewählt
  useEffect(() => {
    if (!customer) return;
    setDebtor(inputFromPreview(customer.debtor));
    setEditDebtor(customer.debtor.missing.length > 0);
    setSelectedInvoices(customer.invoices.filter((i) => i.handoverStatus !== 'HANDED_OVER').map((i) => i.id));
  }, [customer]);

  useEffect(() => {
    if (!stripeCustomerId) {
      setConfirmHandover(false);
      setDecision(null);
      setNote('');
    }
  }, [stripeCustomerId]);

  const state = customer?.workflowState;
  const canHandOver =
    handoverEnabled && Boolean(state) && ['DECIDE', 'FOLLOW_UP', 'DUNNING', 'AT_DEBTIST', 'PARKED'].includes(state);
  const pendingInvoices = useMemo(
    () => (customer?.invoices ?? []).filter((i) => i.handoverStatus !== 'HANDED_OVER'),
    [customer]
  );
  const selectedTotal = pendingInvoices
    .filter((i) => selectedInvoices.includes(i.id))
    .reduce((sum, i) => sum + i.amountRemaining, 0);
  const missing = debtorMissing(debtor);
  const recommendation = customer ? RECOMMENDATION_LABELS[customer.recommendation] : null;
  const workflow = customer ? WORKFLOW_LABELS[customer.workflowState] : null;

  const runAnswer = async (comment: string) => {
    if (!customer) return false;
    try {
      await answerFeedback({
        variables: {
          stripeCustomerId: customer.stripeCustomerId,
          comment,
          contactEmail: debtor.email || customer.email,
        },
      });
      enqueueSnackbar('Antwort an Debtist gesendet', { variant: 'success' });
      afterChange();
      return true;
    } catch (e: any) {
      enqueueSnackbar(`Antwort nicht gesendet: ${e.message}`, { variant: 'error' });
      return false;
    }
  };

  const openDirectPayment = (invoice: DebtInvoice) => {
    setPaymentInvoice(invoice);
    setPaymentAmount(invoice.amountRemaining.toFixed(2).replace('.', ','));
    setPaymentDate(inDays(0));
    setNote('');
  };

  const runDirectPayment = async () => {
    if (!paymentInvoice) return;
    const amount = Number(paymentAmount.replace(/\./g, '').replace(',', '.'));
    try {
      const result = await reportPayment({
        variables: { stripeInvoiceId: paymentInvoice.id, amount, paymentDate, note: note || null },
      });
      enqueueSnackbar(
        result.data?.debtReportDirectPayment
          ? 'Zahlung an Debtist gemeldet, Rechnung in Stripe als bezahlt markiert'
          : 'Teilzahlung an Debtist gemeldet',
        { variant: 'success' }
      );
      setPaymentInvoice(null);
      setNote('');
      afterChange();
    } catch (e: any) {
      enqueueSnackbar(`Zahlung nicht gemeldet: ${e.message}`, { variant: 'error' });
    }
  };

  const openWithdraw = () => {
    if (!customer) return;
    setNote('');
    setWithdrawOpen(true);
    loadFees({ variables: { stripeCustomerId: customer.stripeCustomerId } });
  };

  const runWithdraw = async () => {
    if (!customer) return;
    try {
      const result = await withdraw({
        variables: { stripeCustomerId: customer.stripeCustomerId, reason: withdrawReason, note: note || null },
      });
      enqueueSnackbar(`${result.data?.debtWithdraw ?? 0} Rechnung(en) bei Debtist zurückgezogen`, {
        variant: 'success',
      });
      setWithdrawOpen(false);
      setNote('');
      afterChange();
    } catch (e: any) {
      enqueueSnackbar(`Zurückziehen fehlgeschlagen: ${e.message}`, { variant: 'error' });
    }
  };

  const afterChange = () => {
    refetch();
    onChanged();
  };

  const runHandover = async () => {
    if (!customer) return;
    try {
      const result = await handOver({
        variables: {
          stripeCustomerId: customer.stripeCustomerId,
          invoiceIds: selectedInvoices,
          debtor,
          note: note || undefined,
        },
      });
      const r = result.data?.debtHandOver;
      enqueueSnackbar(
        r?.failed
          ? `${r.handedOver + r.alreadyThere} übergeben, ${r.failed} fehlgeschlagen`
          : `${(r?.handedOver ?? 0) + (r?.alreadyThere ?? 0)} Rechnung(en) an Debtist übergeben${
              r?.debtistReference ? ` (Az. ${r.debtistReference})` : ''
            }`,
        { variant: r?.failed ? 'warning' : 'success' }
      );
      setConfirmHandover(false);
      setNote('');
      afterChange();
    } catch (e: any) {
      enqueueSnackbar(`Übergabe fehlgeschlagen: ${e.message}`, { variant: 'error' });
    }
  };

  const runDecision = async (value: DebtDecision) => {
    if (!customer) return;
    try {
      await decide({
        variables: {
          stripeCustomerId: customer.stripeCustomerId,
          decision: value,
          note: note || null,
          holdUntil: value === 'HOLD' ? new Date(holdUntil).toISOString() : null,
          contactEmail: debtor.email || customer.email,
        },
      });
      enqueueSnackbar('Entscheidung gespeichert', { variant: 'success' });
      setDecision(null);
      setNote('');
      afterChange();
    } catch (e: any) {
      enqueueSnackbar(`Nicht gespeichert: ${e.message}`, { variant: 'error' });
    }
  };

  return (
    <Dialog open={Boolean(stripeCustomerId)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {customer?.name ?? 'Kundenakte'}
        {workflow && <Chip label={workflow.label} color={workflow.color} size="small" variant="outlined" />}
        {recommendation && (
          <Chip label={`Empfehlung: ${recommendation.label}`} color={recommendation.color} size="small" />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading && !file && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CircularProgress size={20} /> Lade Stripe, Mails und Support-Verlauf …
          </div>
        )}
        {error && <div style={{ color: '#c62828' }}>Fehler beim Laden: {error.message}</div>}
        {!loading && !error && !file && <div>Für diesen Kunden gibt es keine offenen Rechnungen mehr.</div>}
        {customer?.caseNote && (
          <div style={{ marginBottom: 16, fontSize: '0.875rem' }}>
            Letzte Entscheidung {formatDate(customer.caseDecidedAt)} von {customer.caseDecidedBy}: {customer.caseNote}
          </div>
        )}
        {file && customer?.debtistReference && (
          <DebtistSection file={file} canAnswer={handoverEnabled} answering={answering} onAnswer={runAnswer} />
        )}
        {file && (
          <CaseFileContent
            file={file}
            debtor={debtor}
            onDebtorChange={setDebtor}
            editDebtor={editDebtor}
            onEditDebtor={() => setEditDebtor(true)}
            selectedInvoices={selectedInvoices}
            onToggleInvoice={(id) =>
              setSelectedInvoices((current) =>
                current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
              )
            }
            canSelect={canHandOver && pendingInvoices.length > 0}
            onDirectPayment={handoverEnabled ? openDirectPayment : undefined}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {customer && state === 'AT_DEBTIST' && handoverEnabled && (
          <Button disabled={withdrawing} onClick={openWithdraw}>
            Zurückziehen …
          </Button>
        )}
        {customer && (state === 'PARKED' || state === 'WRITTEN_OFF' || state === 'CLOSED') && (
          <Button disabled={deciding} onClick={() => runDecision('REOPEN')}>
            Wieder öffnen
          </Button>
        )}
        {customer && ['DECIDE', 'FOLLOW_UP', 'DUNNING'].includes(state) && (
          <>
            <Button disabled={deciding} onClick={() => setDecision('HOLD')}>
              Zurückstellen …
            </Button>
            <Button disabled={deciding} onClick={() => setDecision('CLARIFY')}>
              Selbst klären …
            </Button>
            <Button disabled={deciding} onClick={() => setDecision('WRITE_OFF')}>
              Ausbuchen …
            </Button>
          </>
        )}
        <span style={{ flex: 1 }} />
        {customer && !handoverEnabled && (
          <span style={{ fontSize: '0.8rem', color: '#888' }}>Übergabe an Debtist ist nicht konfiguriert</span>
        )}
        <Button onClick={onClose}>Schließen</Button>
        {canHandOver && pendingInvoices.length > 0 && (
          <Button
            variant="contained"
            disabled={handingOver || selectedInvoices.length === 0 || missing.length > 0}
            onClick={() => setConfirmHandover(true)}
          >
            {selectedInvoices.length} Rechnung(en) an Debtist übergeben
          </Button>
        )}
      </DialogActions>

      <Dialog open={confirmHandover} onClose={() => setConfirmHandover(false)} maxWidth="sm" fullWidth>
        <DialogTitle>An Debtist übergeben?</DialogTitle>
        <DialogContent>
          <p>
            {selectedInvoices.length} Rechnung(en) über <strong>{formatEur(selectedTotal)}</strong> von{' '}
            <strong>{debtor.businessName}</strong> gehen an <strong>{handoverTargetLabel(handoverTarget)}</strong>,
            Stufe Inkasso (Precourt). Stripe bucht diese Rechnungen danach nicht mehr ab, eine laufende Mahnkette endet.
            Das Abo läuft weiter.
          </p>
          <TextField
            fullWidth
            size="small"
            label="Notiz (optional, nur intern)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmHandover(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={runHandover} disabled={handingOver}>
            {handingOver ? <CircularProgress size={18} /> : 'Übergeben'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(paymentInvoice)} onClose={() => setPaymentInvoice(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Direktzahlung melden</DialogTitle>
        <DialogContent>
          <p style={{ fontSize: '0.875rem' }}>
            Der Kunde hat Rechnung {paymentInvoice?.number} direkt an uns bezahlt. Debtist bekommt die Zahlung gemeldet.
            Bei vollem Betrag wird die Rechnung in Stripe als bezahlt markiert.
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <TextField
              size="small"
              label="Betrag (€)"
              value={paymentAmount}
              onChange={(event) => setPaymentAmount(event.target.value)}
            />
            <TextField
              type="date"
              size="small"
              label="Zahlungsdatum"
              value={paymentDate}
              onChange={(event) => setPaymentDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </div>
          <TextField
            fullWidth
            size="small"
            label="Notiz (optional)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentInvoice(null)}>Abbrechen</Button>
          <Button variant="contained" disabled={reporting} onClick={runDirectPayment}>
            Melden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={withdrawOpen} onClose={() => setWithdrawOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bei Debtist zurückziehen?</DialogTitle>
        <DialogContent>
          <p style={{ fontSize: '0.875rem' }}>
            Alle Rechnungen dieses Kunden werden bei Debtist zurückgezogen. In Stripe bleiben sie uneinbringlich.
          </p>
          <div style={{ margin: '12px 0', fontSize: '0.875rem' }}>
            {loadingFees && <CircularProgress size={16} />}
            {feesError && <span style={{ color: '#c62828' }}>Gebühren nicht abrufbar: {feesError.message}</span>}
            {feesData && (
              <strong>
                Stornogebühr: {formatEur(feesData.debtWithdrawalFees.cancellationFee)}
                {feesData.debtWithdrawalFees.successFee
                  ? ` · Erfolgsprovision: ${formatEur(feesData.debtWithdrawalFees.successFee)}`
                  : ''}
              </strong>
            )}
          </div>
          <TextField
            select
            fullWidth
            size="small"
            label="Grund"
            value={withdrawReason}
            onChange={(event) => setWithdrawReason(event.target.value as WithdrawReason)}
            SelectProps={{ native: true }}
          >
            {Object.entries(WITHDRAW_REASON_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </TextField>
          <TextField
            fullWidth
            size="small"
            label="Notiz (optional, nur intern)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawOpen(false)}>Abbrechen</Button>
          <Button variant="contained" color="error" disabled={withdrawing || loadingFees} onClick={runWithdraw}>
            Zurückziehen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(decision)} onClose={() => setDecision(null)} maxWidth="sm" fullWidth>
        {decision && (
          <>
            <DialogTitle>{DECISION_TEXT[decision].title}</DialogTitle>
            <DialogContent>
              {decision === 'HOLD' && (
                <TextField
                  type="date"
                  size="small"
                  label="Zurückstellen bis"
                  value={holdUntil}
                  onChange={(event) => setHoldUntil(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 1, mb: 2 }}
                />
              )}
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label={DECISION_TEXT[decision].noteLabel}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                sx={{ mt: 1 }}
              />
              {decision === 'WRITE_OFF' && (
                <p style={{ color: '#888', fontSize: '0.875rem', marginTop: 12 }}>
                  Ausbuchen ändert nichts in Stripe, die Rechnungen bleiben uneinbringlich. Neue offene Rechnungen
                  erscheinen später wieder als Nachforderung.
                </p>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDecision(null)}>Abbrechen</Button>
              <Button variant="contained" disabled={deciding} onClick={() => runDecision(decision)}>
                {DECISION_TEXT[decision].button}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Dialog>
  );
}
